import React, { useEffect, useMemo, useState } from "react";
import { BrandMark, Button, Field, SelectField } from "@nelurio/ui";
import { languages, useI18n } from "@nelurio/i18n";
import { useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { API_BASE_URL, api, isDemoMode } from "../api/client";
import { BookIllustration } from "../components/BookIllustration";

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62Z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18Z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z" />
  </svg>
);

type Mode = "login" | "register" | "phone" | "forgot";

export function AuthPage() {
  const { t, locale, setLocale } = useI18n();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState<{ google: boolean; apple: boolean; sms: boolean; emailReset: boolean } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("oauthError");
    if (oauthError === "provider_not_configured") setError(t("auth.oauthNotConfigured"));
    else if (oauthError) setError(t("auth.oauthError"));
    if (oauthError) window.history.replaceState({}, "", window.location.pathname);
    if (isDemoMode()) return;
    fetch(`${API_BASE_URL}/auth/oauth/providers`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => setProviders(payload?.data ?? payload ?? { google: false, apple: false, sms: false, emailReset: false }))
      .catch(() => setProviders({ google: false, apple: false, sms: false, emailReset: false }));
  }, [t]);

  useEffect(() => {
    if (mode === "phone" && providers && !providers.sms) {
      setMode("login");
      setCodeSent(false);
    }
  }, [providers, mode]);

  const oauthStart = (provider: "google") => {
    window.location.href = `${API_BASE_URL}/auth/oauth/${provider}/start?locale=${encodeURIComponent(locale)}`;
  };

  const divider = useMemo(() => <div className="auth-divider"><span>{t("auth.or")}</span></div>, [t]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/");
      } else if (mode === "register") {
        await register(displayName, email, password);
        navigate("/");
      } else if (mode === "phone") {
        if (!codeSent) {
          const result = await api<{ phone: string }>("/auth/phone/request", { method: "POST", body: JSON.stringify({ phone }) });
          setCodeSent(true);
          setNotice(t("auth.codeSentTo", { target: result.phone }));
        } else {
          await api("/auth/phone/verify", { method: "POST", body: JSON.stringify({ phone, code, displayName }) });
          window.location.href = "/";
        }
      } else if (mode === "forgot") {
        if (!codeSent) {
          await api("/auth/password/forgot", { method: "POST", body: JSON.stringify({ email }) });
          setCodeSent(true);
          setNotice(t("auth.resetCodeSent", { email }));
        } else {
          await api("/auth/password/reset", { method: "POST", body: JSON.stringify({ email, code, newPassword: password }) });
          setNotice(t("auth.resetSuccess"));
          setMode("login");
          setCodeSent(false);
          setCode("");
        }
      }
    } catch (reason) {
      setError(localizeAuthError(reason, t));
    } finally {
      setSubmitting(false);
    }
  };

  const smsAvailable = !providers || providers.sms;
  const methodTabs = (
    <div className="auth-methods" role="tablist">
      <button type="button" role="tab" aria-selected={mode !== "phone"} className={mode !== "phone" ? "is-active" : ""} onClick={() => { setMode("login"); setError(""); setNotice(""); setCodeSent(false); }}>{t("auth.methodEmail")}</button>
      {smsAvailable && (
        <button type="button" role="tab" aria-selected={mode === "phone"} className={mode === "phone" ? "is-active" : ""} onClick={() => { setMode("phone"); setError(""); setNotice(""); setCodeSent(false); }}>{t("auth.methodPhone")}</button>
      )}
    </div>
  );

  return (
    <main className="auth-page">
      <section className="auth-visual">
        <BrandMark />
        <div className="auth-visual__content">
          <BookIllustration />
          <div className="auth-visual__copy">
            <h1>{t("brand.tagline")}</h1>
            <p>{t("brand.taglineBody")}</p>
          </div>
        </div>
        <small>
          © 2026 Nelurio ·{" "}
          <a className="auth-legal-link" href="/legal/imprint">{t("auth.imprintLink")}</a> ·{" "}
          <a className="auth-legal-link" href="/legal/privacy">{t("auth.privacyLink")}</a>
        </small>
      </section>

      <section className="auth-form-panel">
        <div className="auth-form-card">
          <div className="auth-form-card__brand"><BrandMark compact /></div>
          <span className="section-kicker">Nelurio</span>
          <h2>{mode === "forgot" ? t("auth.forgotTitle") : t("auth.welcome")}</h2>
          <p>{mode === "forgot" ? t("auth.forgotLead") : t("auth.subtitle")}</p>

          {mode !== "forgot" && methodTabs}

          {mode !== "phone" && !isDemoMode() && (
            <>
              {providers?.google && (
                <>
                  <div className="oauth-buttons">
                    {providers?.google && (
                      <button type="button" className="oauth-button" onClick={() => oauthStart("google")}>
                        <GoogleMark /><span>{t("auth.continueGoogle")}</span>
                      </button>
                    )}

                  </div>
                  {divider}
                </>
              )}
            </>
          )}

          <form onSubmit={submit}>
            {mode === "register" && <Field label={t("auth.nameField")} value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder={t("auth.namePlaceholder")} required autoComplete="name" />}

            {(mode === "login" || mode === "register" || (mode === "forgot" && !codeSent)) && (
              <Field label={t("auth.email")} type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={t("auth.emailPlaceholder")} required autoComplete="email" />
            )}
            {mode === "phone" && (
              <Field label={t("auth.phone")} value={phone} onChange={(event) => setPhone(event.target.value)} placeholder={t("auth.phonePlaceholder")} required autoComplete="tel" />
            )}
            {mode === "phone" && codeSent && (
              <>
                <Field label={t("auth.code")} value={code} onChange={(event) => setCode(event.target.value)} placeholder="------" required inputMode="numeric" maxLength={6} />
                <Field label={t("auth.nameField")} value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder={t("auth.namePlaceholder")} autoComplete="name" />
              </>
            )}
            {(mode === "login" || mode === "register") && (
              <Field label={t("auth.password")} type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} autoComplete={mode === "login" ? "current-password" : "new-password"} />
            )}
            {mode === "forgot" && codeSent && (
              <>
                <Field label={t("auth.code")} value={code} onChange={(event) => setCode(event.target.value)} placeholder="------" required inputMode="numeric" maxLength={6} />
                <Field label={t("auth.newPassword")} type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} autoComplete="new-password" />
              </>
            )}

            {providers?.emailReset !== false && (
              <button type="button" className="auth-forgot" onClick={() => { setMode("forgot"); setCodeSent(false); setError(""); setNotice(""); }}>
                {t("auth.forgot")}
              </button>
            )}

            <SelectField label={t("auth.language")} value={locale} onChange={(event) => setLocale(event.target.value as never)}>
              {languages.map((language) => <option key={language.code} value={language.code}>{language.nativeLabel}</option>)}
            </SelectField>

            {notice && <div className="settings-notice" role="status">{notice}</div>}
            {error && <div className="form-error" role="alert">{error}</div>}

            <Button type="submit" loading={submitting}>
              {mode === "login" ? t("auth.signIn")
                : mode === "register" ? t("auth.createAccount")
                : mode === "phone" ? (codeSent ? t("auth.verifyCode") : t("auth.requestCode"))
                : (codeSent ? t("auth.setPassword") : t("auth.sendReset"))}
            </Button>
          </form>

          {mode !== "forgot" ? (
            <button type="button" className="auth-mode-switch" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
              {mode === "login" ? `${t("auth.noAccount")} ${t("auth.createAccount")}` : `${t("auth.haveAccount")} ${t("auth.signIn")}`}
            </button>
          ) : (
            <button type="button" className="auth-mode-switch" onClick={() => { setMode("login"); setCodeSent(false); setError(""); setNotice(""); }}>
              {t("auth.backToLogin")}
            </button>
          )}
        </div>
      </section>
    </main>
  );
}

import type { ApiClientError } from "../api/client";

/** Server messages are English; map known auth codes to the user's language. */
function localizeAuthError(reason: unknown, t: (key: string) => string): string {
  const code = (reason as Partial<ApiClientError>)?.code;
  const map: Record<string, string> = {
    email_unconfigured: "auth.errEmailUnconfigured",
    sms_unconfigured: "auth.errSmsUnconfigured",
    invalid_code: "auth.errInvalidCode",
    invalid_credentials: "auth.errInvalidCredentials"
  };
  return code && map[code] ? t(map[code]) : reason instanceof Error && reason.message ? reason.message : t("auth.loginError");
}
